/**
 * CreateWorkshopLauncher — app-shell-level provider for the GLOBAL "New
 * workshop" entry point (BON-203 round 2). Owns the open-state for a
 * region-less CreateWorkshopDrawer (G3), exposes `openCreateWorkshop()` to the
 * command palette (G1), and registers the global keyboard shortcut (G2) with a
 * typing-guard. The regional-calendar trigger is unaffected — it mounts its
 * own drawer with a pre-set region.
 *
 * Gating: the launcher only opens for viewers with write:workshops
 * (operators/RDs), so the palette command + shortcut are no-ops for teachers.
 */
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';

import type { RegionNode } from '../../../../api/regions';
import type { WorkshopDetail } from '../../../../api/workshops';
import { useCreateShortcut } from '../../../../hooks/use-create-shortcut';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Permission } from '../../../../types/permissions';
import { regionSlugFromParam } from '../../primitives/scope/region-param';
import { useWritableRegions } from '../../primitives/use-writable-regions';
import { workshopKey } from '../useWorkshop';

import CreateWorkshopDrawer from './CreateWorkshopDrawer';

/**
 * The global shortcut is a Linear/Gmail-style key SEQUENCE: press "c" then "w"
 * (create → workshop). A sequence sidesteps the browser-reserved modifier
 * chords (Ctrl/Cmd+W closes the window, etc.). The two keys must land close
 * together — see `useCreateShortcut`.
 *
 * The keystrokes are surfaced as DISTINCT chips (Q3) — `NEW_WORKSHOP_SHORTCUT_KEYS`
 * is the canonical source the palette renders one kbd per key; the LABEL is a
 * human-readable string for titles/tooltips.
 */
export const NEW_WORKSHOP_SHORTCUT_KEYS = ['C', 'W'] as const;
export const NEW_WORKSHOP_SHORTCUT_LABEL = NEW_WORKSHOP_SHORTCUT_KEYS.join(' then ');

interface LauncherValue {
  /** Open the global (region-less) create-workshop drawer. No-op without
   *  write permission. */
  openCreateWorkshop: () => void;
  /** Whether the viewer may create workshops — gates the palette command. */
  canCreateWorkshop: boolean;
}

const CreateWorkshopLauncherContext = createContext<LauncherValue>({
  openCreateWorkshop: () => {},
  canCreateWorkshop: false,
});

/** Resolve a region slug to a writable-region id (the tree carries `slug`). */
function regionIdForSlug(regions: RegionNode[], slug: string | undefined): string {
  if (!slug) return '';
  const walk = (nodes: RegionNode[]): string => {
    for (const n of nodes) {
      if (n.slug === slug) return n.id;
      const found = walk(n.children);
      if (found) return found;
    }
    return '';
  };
  return walk(regions);
}

/** The contextual SOFT (prefill) Location seed derived from the current URL:
 *  region from the page's own `?region=` scope; venue from an
 *  `/admin/venues/:venueId` match, OR — on a workshop detail page — from the
 *  already-fetched WorkshopDetail in the react-query cache (round 9). Both are
 *  editable pre-fills; an uncached workshop degrades to region-only.
 *
 *  The region used to come from a `/r/:slug` path segment. Every admin page
 *  scopes itself by query parameter now, so that is where the seed comes from
 *  — one reader for every scoped page rather than a path shape per tree. */
function useContextualLocation(regions: RegionNode[]) {
  // The launcher sits under the Router but not under a matched Route, so read
  // the current path via useMatch (NOT useParams).
  // Trailing /* so the match survives the venue detail tab segment
  // (/venues/:venueId/:tab, BON-235) — named params still resolve.
  const adminVenue = useMatch('/admin/venues/:venueId/*');
  const adminWorkshop = useMatch('/admin/workshops/:workshopId/*');
  const [params] = useSearchParams();
  const qc = useQueryClient();

  const regionId = regionIdForSlug(regions, regionSlugFromParam(params.get('region')));

  // Venue from a venue route, else opportunistically from the cached workshop
  // the detail page already loaded (no extra fetch; skip if not cached).
  const workshopId = adminWorkshop?.params.workshopId;
  const cachedVenueId = workshopId
    ? (qc.getQueryData<WorkshopDetail>(workshopKey(workshopId))?.venueId ?? '')
    : '';
  const venueId = adminVenue?.params.venueId ?? cachedVenueId;

  return useMemo(
    () => ({
      regionId,
      venueId,
      regionMode: regionId ? ('prefill' as const) : ('open' as const),
      // A venue pre-fill stands on its own: when the URL carries no region slug
      // (`/admin/venues/:id`) the drawer adopts the venue's own region.
      venueMode: venueId ? ('prefill' as const) : ('open' as const),
    }),
    [regionId, venueId],
  );
}

export function CreateWorkshopLauncherProvider({ children }: { children: ReactNode }) {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  // Withheld until the token has decoded. `hasPermission` answers false while
  // it is still loading, so deciding early registers the shortcut and the
  // palette command a beat after first paint rather than with it.
  const canCreateWorkshop = !permissionsLoading && hasPermission(Permission.WRITE_WORKSHOPS);
  const [open, setOpen] = useState(false);
  const { regions, loading: regionsLoading, failed: regionsFailed } = useWritableRegions();
  const contextual = useContextualLocation(regions);

  const openCreateWorkshop = useCallback(() => {
    if (canCreateWorkshop) setOpen(true);
  }, [canCreateWorkshop]);

  // G2 — global "c then w" sequence (create → workshop).
  useCreateShortcut('w', canCreateWorkshop, () => setOpen(true));

  const value = useMemo(
    () => ({ openCreateWorkshop, canCreateWorkshop }),
    [openCreateWorkshop, canCreateWorkshop],
  );

  return (
    <CreateWorkshopLauncherContext.Provider value={value}>
      {children}
      {open && (
        <CreateWorkshopDrawer
          shell="admin"
          regionId={contextual.regionId || null}
          regionMode={contextual.regionMode}
          venueId={contextual.venueId || null}
          venueMode={contextual.venueMode}
          regions={regions}
          regionsLoading={regionsLoading}
          regionsFailed={regionsFailed}
          onClose={() => setOpen(false)}
        />
      )}
    </CreateWorkshopLauncherContext.Provider>
  );
}

export function useCreateWorkshopLauncher(): LauncherValue {
  return useContext(CreateWorkshopLauncherContext);
}
